import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Collapse,
    IconButton,
    Box
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import LoadingSpinner from '../../../../../animation/LoadingSpinner';
import { H6, Body2, Body3, Body1 } from '../../../../../themes/typography';
import '../../../../../styles/components/Invitation.css';

function AttendanceList({
    attendanceList,
    loading
}) {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [data, setData] = useState([...attendanceList]);
    const [openRows, setOpenRows] = useState(null);

    useEffect(() => {
        setData([...attendanceList]);
    }, [attendanceList]);

    const handleSort = () => {
        const isAsc = orderBy === 'name' && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy('name');

        const sortedData = [...data].sort((a, b) => {
            if (newOrder === 'asc') {
                return a['name'].localeCompare(b['name']);
            } else {
                return b['name'].localeCompare(a['name']);
            }
        });

        setData(sortedData);
    };

    return (
        <div className="invitation-list-container">
            <TableContainer sx={tableContainerStyle}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead sx={dataCellStyle}>
                        <TableRow sx={dataCellStyle}>
                            <TableCell align="left" sx={{ ...headerCellStyle, width: '30%' }}>
                                <TableSortLabel
                                    direction={order}
                                    active={orderBy === 'name'} // to hide the sort icon and only show on
                                    onClick={handleSort}
                                    sx={{
                                        gap: '0.5rem',
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'var(--secondary-color) !important'
                                        }
                                    }}
                                >
                                    <H6>
                                        Customer Details
                                    </H6>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" sx={{ ...headerCellStyle, width: '20%' }}>
                                <H6>
                                    Ticket Id
                                </H6>
                            </TableCell>
                            <TableCell align="center" sx={{ ...headerCellStyle, width: '20%' }}>
                                <H6>
                                    Ticket Name
                                </H6>
                            </TableCell>
                            <TableCell align="center" sx={{ ...headerCellStyle, width: '20%' }}>
                                <H6>
                                    Status
                                </H6>
                            </TableCell>
                            <TableCell sx={{ ...headerCellStyle, width: '15%' }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && data.map((row, index) => {
                            const isOpen = openRows === index;

                            return (
                                <>
                                    <TableRow
                                        hover={false}
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={index}
                                        sx={{
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <TableCell align="left" sx={isOpen ? dataCellBorderNone : dataCellStyle}>
                                            <div style={attendeeDetail}>
                                                <Body1>{row.attendee_name}</Body1>
                                                <Body3>{row.attendee_email}</Body3>
                                                <Body3>{row.attendee_phone}</Body3>
                                            </div>
                                        </TableCell>
                                        <TableCell align="center" sx={isOpen ? dataCellBorderNone : dataCellStyle}>
                                            <Body2>
                                                {row.ticket_number}
                                            </Body2>
                                        </TableCell>
                                        <TableCell align="center" sx={isOpen ? dataCellBorderNone : dataCellStyle}>
                                            <Body2>
                                                {row.ticket_type}
                                            </Body2>
                                        </TableCell>

                                        <TableCell align="center" sx={isOpen ? dataCellBorderNone : dataCellStyle}>
                                            {(() => {
                                                const history = row.scan_history || [];
                                                if (history.length === 0) return <Body2>-</Body2>;

                                                const firstAction = history[0]?.action;
                                                const checkedInCount = history.filter(
                                                    (item) => item.action === "checked_in"
                                                ).length;

                                                if (firstAction === "rejected") {
                                                    return (
                                                        <Body2 color="var(--error-main-color)">
                                                            Rejected
                                                        </Body2>
                                                    );
                                                }

                                                if (firstAction === "checked_in" && checkedInCount > 1) {
                                                    return (
                                                        <Body2 color="var(--secondary-color)">
                                                            Multiple Checked In
                                                        </Body2>
                                                    );
                                                }

                                                if (firstAction === "checked_in") {
                                                    return (
                                                        <Body2 color="var(--success-secondary-color)">
                                                            Checked In
                                                        </Body2>
                                                    );
                                                }

                                                return <Body2>-</Body2>;
                                            })()}
                                        </TableCell>

                                        <TableCell sx={isOpen ? dataCellBorderNone : dataCellStyle}>
                                            <IconButton
                                                aria-label="expand row"
                                                size="small"
                                                onClick={() => setOpenRows(isOpen ? null : index)}
                                            >
                                                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>

                                    </TableRow>

                                    <HistoryTable history={row.scan_history} openRows={isOpen} />
                                </>
                            );
                        })}


                        {!loading && data?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={dataCellStyle}
                                >
                                    <Body1>No attendees record found.</Body1>
                                </TableCell>
                            </TableRow>
                        )}

                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={dataCellBorderNone}>
                                    <LoadingSpinner
                                        message='Loading attendance list...'
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

export default AttendanceList;

const attendeeDetail = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
};


function HistoryTable({ history, openRows }) {
    return (
        <>
            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    sx={openRows ? dataCellStyle : dataCellBorderNone}
                    colSpan={6}
                >
                    <Collapse in={openRows} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <H6>
                                Scan History
                            </H6>

                            <Table size="small" aria-label="purchases" sx={scanHistoryTable}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...headerCellStyle, padding: '0.6rem 1rem' }}>
                                            <Body2>
                                                No.
                                            </Body2>
                                        </TableCell>
                                        <TableCell sx={headerCellStyle} align='center'>
                                            <Body2>
                                                Scanned By
                                            </Body2>
                                        </TableCell>

                                        <TableCell sx={headerCellStyle} align='center'>
                                            <Body2>
                                                Action
                                            </Body2>
                                        </TableCell>

                                        <TableCell sx={headerCellStyle} align='center'>
                                            <Body2>
                                                Scanned On
                                            </Body2>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.length > 0 && history.map((record, index) => (
                                        <TableRow key={index}>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={{ ...dataCellStyle, padding: '0.6rem 1rem' }}
                                            >
                                                <Body2>
                                                    {index + 1}.
                                                </Body2>
                                            </TableCell>

                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={dataCellStyle}
                                                align="center"
                                            >
                                                <Body2>
                                                    {record.checked_in_by || '-'}
                                                </Body2>
                                            </TableCell>

                                            <TableCell
                                                sx={dataCellStyle}
                                                align="center"
                                            >

                                                {record.action === 'checked_in' && (
                                                    <Body2 color="var(--success-secondary-color)">
                                                        Checked In
                                                    </Body2>
                                                )}

                                                {record.action === 'rejected' && (
                                                    <Body2 color="var(--error-main-color)">
                                                        Rejected
                                                    </Body2>
                                                )}

                                            </TableCell>

                                            <TableCell
                                                sx={dataCellStyle}
                                                align="center"
                                            >
                                                <Body2>
                                                    {record.action_time || '-'}
                                                </Body2>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

const tableContainerStyle = {
    borderTopRightRadius: '1rem',
    borderTopLeftRadius: '1rem',
    maxHeight: '70vh'
};

const scanHistoryTable = {
    borderTopRightRadius: '1rem',
    borderTopLeftRadius: '1rem',
    overflow: 'hidden',
    marginTop: '0.5rem'
};

const headerCellStyle = {
    backgroundColor: '#283853',
    borderBottom: 'none'
};

const dataCellStyle = {
    borderBottom: '1px solid var(--divider-color)'
};

const dataCellBorderNone = {
    borderBottom: 'none'
};